import * as React from 'react';
import { ErrorLogComponent } from '@cian/error-log-component';
import { ITop3ReviewsContext } from '@cian/top3-reviews-widget';
import { UiKitContextProvider } from '@cian/ui-kit/context';
import { uaToDeviceType } from '@cian/ui-kit/utils';
import { Provider } from 'react-redux';

import { ContentWrapper } from '../../components/ContentWrapper';
import { Main } from '../../components/Main';
import { OrderWrapper } from '../../components/OrderWrapper';
import { IApplicationContext } from '../../types/applicationContext';
import { ICustomContextParams } from '../../types/ICustomContextParams';
import { TReduxStore } from '../../types/redux';
import { Context as ApplicationContext } from '../../utils/withApplicationContext';
import { About } from '../About';
import { AddressContainer } from '../Address';
import { AdvantagesContainer } from '../Advantages';
import { AeroPhotoTourEntryContainer } from '../AeroPhotoTourEntry';
import { AeroPhotoTourModalContainer } from '../AeroPhotoTourModal';
import { Banks } from '../Banks';
import { BasicInfoContainer } from '../BasicInfo';
import { BreadCrumbs } from '../BreadCrumbs/BreadCrumbs';
import { BuilderContainer } from '../Builder';
import { BuilderNewbuildingsContainer } from '../BuilderNewbuildings';
import { BuildersAgentsContainer } from '../BuildersAgents';
import { CallbackPopupContainer } from '../CallbackPopup';
import { CarouselContainer } from '../Carousel';
import { ConstructionProgressContainer } from '../ConstructionProgress';
import { DocumentsContainer } from '../Documents';
import { EcologyContainer } from '../Ecology';
import { EscrowContainer } from '../Escrow';
import { FixedHeader } from '../FixedHeader';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { GenplanContainer } from '../Genplan';
import { HousesByTurnSeoContainer } from '../HousesByTurnSeo';
import { ImageSources } from '../ImageSources';
import { InteriorDecorationContainer } from '../InteriorDecoration';
import { LastHopePopupContainer } from '../LastHopePopup';
import { LayoutsContainer } from '../Layouts';
import { MapContainer } from '../Map';
import { MVKContainer } from '../MVK';
import { NewbuildingChatContainer } from '../NewbuildingChat';
import { OffersBlockABContainer } from '../OffersBlockAB';
import { PlacesNearbyContainer } from '../PlacesNearby';
import { Promo } from '../Promo';
import { Promos } from '../Promos';
import { RealtyValuationContainer } from '../RealtyValuation';
import { ReliabilityContainer } from '../Reliability';
import { ReviewsContainer } from '../Reviews';
import { ScrollDown } from '../ScrollDown';
import { SimilarNewbuildingsContainer } from '../SimilarNewbuildings';
import { SpecificationsContainer } from '../Specifications';
import { SpecificationsDetailedSeoContainer } from '../SpecificationsDetailedSeo';
import { TopControlsContainer } from '../TopControls';
import { Video } from '../Video';

export interface IApplicationProps extends IApplicationContext {
  reduxStore: TReduxStore;
  customContext: ICustomContextParams;
  top3ReviewsContext?: ITop3ReviewsContext;
}

export const Application = ({
  config,
  customContext,
  httpApi,
  logger,
  reduxStore,
  top3ReviewsContext,
}: IApplicationProps) => {
  const context = React.useMemo(() => ({ config, httpApi, logger }), [config, httpApi, logger]);

  return (
    <ErrorLogComponent logger={logger}>
      <ApplicationContext.Provider value={context}>
        <UiKitContextProvider deviceType={uaToDeviceType(customContext.userAgent)}>
          <Provider store={reduxStore}>
            <FixedHeader />
            <Main>
              <CarouselContainer />
              <BreadCrumbs />
              <FlatViewOrderTooltipContainer />
              <TopControlsContainer />
              <BasicInfoContainer />
              <ScrollDown />
            </Main>
            <ContentWrapper>
              <Promo />
              <AddressContainer />
              <SpecificationsContainer />
              <SpecificationsDetailedSeoContainer />
              <HousesByTurnSeoContainer />
              <GenplanContainer />
              <OrderWrapper order={1}>
                <About />
              </OrderWrapper>
              <OrderWrapper order={0}>
                <MVKContainer />
                <OffersBlockABContainer />
                <RealtyValuationContainer />
              </OrderWrapper>
              <OrderWrapper order={2}>
                <Video />
                <AdvantagesContainer />
                <MapContainer />
                <AeroPhotoTourEntryContainer />
                <PlacesNearbyContainer />
                <EcologyContainer />
                <LayoutsContainer />
                <InteriorDecorationContainer />
                <BuilderContainer />
                <BuildersAgentsContainer />
                <BuilderNewbuildingsContainer />
                <ConstructionProgressContainer />
                <Banks />
                <Promos />
                <ReliabilityContainer />
                <EscrowContainer />
                <DocumentsContainer />
                <ReviewsContainer context={top3ReviewsContext} />
                <SimilarNewbuildingsContainer />
                <ImageSources />
                <NewbuildingChatContainer config={config} />
                <LastHopePopupContainer />
                <AeroPhotoTourModalContainer />
                <CallbackPopupContainer />
              </OrderWrapper>
            </ContentWrapper>
          </Provider>
        </UiKitContextProvider>
      </ApplicationContext.Provider>
    </ErrorLogComponent>
  );
};
